/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import { PreviewType } from 'client-code/previews';

import { TIVO_SERVICES } from '../../../config';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { TOfferPreview } from '../../../core/types';
import { PreviewComponent, PreviewComponentAssets } from '../../../core/usecases';
import {
    ActionButton,
    Badge,
    ContentDescription,
    ContentTitle,
    Credits,
    Divider,
    Duration,
    Genres,
    ParentalControlRating,
    Separator,
    ShowcaseBanner,
    StarRating,
    StatusMessage,
} from '../ui-kit';
import { InfoCardProps } from './InfoCardProps';

/**
 * MovieInfoCard
 * Ref.: http://w3-engr/MyWorld/UserInterface/Hydra/Specs/STB/core_ux/components/components.html#movie_info_card
 * There is no line limit or truncation enforced for metadata display. Display all metadata
 * available for each part, for example, title, description, or actors list.
 *
 * See redline for layout.
 *
 * Detail mode: Side caption
 *      - Part 0
 *      - Part 1
 *      - Part 2
 *      - Part 3
 *      - Part 4
 *      - Part 5
 *      - Detail mode: Action
 */
const MovieInfoCard: FunctionComponent<InfoCardProps> = ({
    highlight,
    isActionMode,
    metadata,
    userActions,
    properties,
}) => {
    const { offerPreview = {} } = metadata as TOfferPreview;
    const { contentPreview } = offerPreview;
    if (!contentPreview) return null;
    const { previewType } = contentPreview;
    if (previewType !== PreviewType.MoviePreview) return null;

    const { episodePreviewImageUrl = '' } = contentPreview;
    const previewInfo = {
        ...offerPreview,
        ...contentPreview,
    };

    const assetService = new ImageAssetsService({
        imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
    });

    const url: string = assetService.getMoviePosterImageUrl(contentPreview.contentId || '');

    const pcRatingNames = PreviewComponent.getParentalRatingNames(previewInfo?.internalRatingList);
    const oPreviewAssets = new PreviewComponentAssets();
    const sContentDescription =
        contentPreview.description ||
        oPreviewAssets.getStringAssestById(
            PreviewComponentAssets.StringAssets.DESCRIPTION_NOT_AVAILABLE,
        );

    const drawInfoCard = () => {
        return (
            <div className="info-card-container">
                {/* <div className="info-card metadata-only"> 
                to show metadata only in scroll mode */}
                <div className="info-card">
                    <div className="info-card-header">
                        <div
                            className={
                                properties?.hasItemsBefore
                                    ? 'left-nav-arrow'
                                    : 'left-nav-arrow hidden'
                            }
                        ></div>
                        {/* <div className="showcase-banner-metadata">
                        to switch between banner with metadata view */}
                        <div className="uikit showcase-banner-metadata">
                            <ShowcaseBanner
                                bannerUrl={url || episodePreviewImageUrl}
                                fallbackText={contentPreview.title || ''}
                            />
                            <div className="metadata">
                                <div className="side-captions">
                                    <Badge
                                        text={
                                            previewInfo.videoResolution ||
                                            previewInfo.channel?.videoResolution
                                        }
                                        uppercase={true}
                                    />
                                    <Badge text={previewInfo.isNew ? 'New' : ''} />
                                    <ParentalControlRating ratingNames={pcRatingNames} />
                                    <Separator />
                                    <Genres categoryLabel={previewInfo.categoryLabel} />
                                    <Separator />
                                    <span className="uikit primary-text-style muted-color text-light">
                                        {previewInfo.movieYear}
                                    </span>
                                    <Separator />
                                    <Duration duration={previewInfo.duration} />
                                </div>
                                <Credits creditString={previewInfo.creditString} lines={2} />
                                <StarRating starRating={previewInfo.starRating} />
                            </div>
                        </div>
                        <div
                            className={
                                properties?.hasItemsAfter
                                    ? 'right-nav-arrow'
                                    : 'right-nav-arrow hidden'
                            }
                        ></div>
                    </div>
                    <div className="info-card-body">
                        <StatusMessage
                            status={previewInfo.recordingStatus}
                            isKeepForeverRecording={previewInfo.isKeepForeverRecording}
                            showType={previewInfo.previewType}
                            isPartialRecording={previewInfo.isPartialRecording}
                            startTime={previewInfo.startTime}
                            endTime={previewInfo.endTime}
                            duration={previewInfo.recordingDuration}
                            recordingAvailability={previewInfo.recordingAvailability}
                        />
                        <ContentTitle
                            title={contentPreview.title}
                            showType={contentPreview.previewType}
                            year={contentPreview.movieYear}
                        />
                        <ContentDescription description={sContentDescription} />
                    </div>
                    <div className="info-card-footer">
                        <Divider />
                        {!isActionMode && <ActionButton type="options" highlighted={true} />}
                        {isActionMode &&
                            (userActions.length > 0 ? (
                                userActions.map((userAction, idx) => (
                                    <ActionButton
                                        key={userAction.id}
                                        highlighted={idx === highlight}
                                    >
                                        {userAction.text}
                                    </ActionButton>
                                ))
                            ) : (
                                <ActionButton type="info" highlighted={true} />
                            ))}
                    </div>
                </div>
            </div>
        );
    };

    return <React.Fragment>{drawInfoCard()}</React.Fragment>;
};

export default MovieInfoCard;
